import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

export interface ReclassifyReplaceExistingFileDialogProps {
  isOpen: boolean;
  slotLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ReclassifyReplaceExistingFileDialog({
  isOpen,
  slotLabel,
  onCancel,
  onConfirm,
}: ReclassifyReplaceExistingFileDialogProps) {
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid="reimbursement-reclassify-replace-confirm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg animate-slideUp overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reimbursement-reclassify-replace-title"
        aria-describedby="reimbursement-reclassify-replace-description"
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-amber-600">
              <AlertTriangle size={22} aria-hidden="true" />
            </div>
            <h2
              id="reimbursement-reclassify-replace-title"
              className="text-xl font-bold text-gray-900"
            >
              Replace existing file?
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label="Close"
            data-testid="reimbursement-reclassify-replace-close"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div
          className="space-y-4 p-6"
          id="reimbursement-reclassify-replace-description"
        >
          <p className="text-sm leading-relaxed text-gray-700">
            There is already a file in the{" "}
            <span className="font-semibold text-gray-900">{slotLabel}</span>{" "}
            slot. Reclassifying will permanently delete it.
          </p>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            If you want to keep the existing file, cancel and reclassify it to
            another slot first.
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:rounded-b-xl">
          <button
            type="button"
            onClick={onCancel}
            data-testid="reimbursement-reclassify-replace-cancel"
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="reimbursement-reclassify-replace-discard"
            className="w-full rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 sm:w-auto"
          >
            Discard &amp; Reclassify
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
